import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { ParamsType } from '../services/resonanceApi';

interface Options<T = any> {
  enabled?: boolean,
  onSuccess?: (data: T) => void,
  // Seed data to render synchronously on mount (e.g. from a localStorage cache). When
  // provided, the hook renders it immediately and revalidates in the background instead of
  // showing a loading state — and a failed revalidation keeps the seeded data rather than
  // surfacing an error. Without it, the hook behaves exactly as before.
  initialData?: T
}

type Handler = (arg: any) => Promise<{ data: any }>

export const useFetch = <T = any>(key: string, handler: Handler, params: ParamsType, options: Options<T> = {}) => {
  const hasSeed = options.initialData !== undefined;
  const [isLoading, setIsLoading] = useState(!hasSeed);
  const [isError, setIsError] = useState(false);
  const [data, setData] = useState<T | undefined>(options.initialData);
  const [error, setError] = useState<AxiosError>();

  useEffect(function () {

    async function LoadData() {
      try {
        if (options.enabled === false) return;
        // With a seed already rendered, revalidate silently (no loading flash).
        if (!hasSeed) setIsLoading(true);
        const { data } = await handler(params);
        setData(data);
        setIsError(false);
        setError(undefined);
        if (options.onSuccess) options.onSuccess(data);
      }
      catch (e) {
        // Keep the seeded/last-good render on a background revalidation failure.
        if (!hasSeed) setIsError(true);
        setError(error)
      }
      finally {
        setIsLoading(false)
      }
    }

    LoadData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...Object.values(params), options.enabled])

  return { data, isLoading, isError, error }
};
